import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, FileText, Lock, ShieldCheck, Eye } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import api from "@/lib/api";
import { toast } from "sonner";

function formatBytes(b) {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-MY", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}

export function NoteCard({ note }) {
  const [busyMode, setBusyMode] = useState(null); // null | "attachment" | "inline"
  const available = note.status === "tersedia" && !!note.file_url;
  const fileName = note.display_name || note.original_filename;

  const trigger = async (mode) => {
    if (!available || busyMode) return;
    setBusyMode(mode);
    try {
      const { data } = await api.post(`/notes/${note.id}/download`, null, { params: { mode } });
      if (!data?.download_url) {
        toast.error("PDF is not available at this time. Please contact admin.");
        return;
      }
      const url = data.download_url.startsWith("/api/")
        ? `${process.env.REACT_APP_BACKEND_URL}${data.download_url}`
        : data.download_url;
      window.open(url, "_blank", "noopener,noreferrer");
      if (mode === "inline") {
        toast.success("Opening PDF...");
      } else {
        toast.success(data.watermarked ? "Note downloaded (watermarked)." : "Note is being downloaded...");
      }
    } catch (err) {
      const msg = err.response?.data?.detail;
      const text = typeof msg === "string" ? msg : "PDF is not available at this time. Please contact admin.";
      toast.error(text);
    } finally {
      setBusyMode(null);
    }
  };

  return (
    <Card className="glass-card glass-card-hover" data-testid={`note-card-${note.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <FileText className="h-5 w-5 text-primary" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-xs font-mono font-semibold text-primary tracking-wider">{note.code}</span>
            <h3 className="font-heading font-semibold text-base mt-0.5 leading-tight truncate">{note.title}</h3>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">PDF</span>
              <span className="text-muted-foreground">·</span>
              <StatusBadge status={note.status} />
              {available && (
                <span className="text-[10px] text-teal inline-flex items-center gap-1">
                  <ShieldCheck className="h-3 w-3" /> Watermarked
                </span>
              )}
            </div>
            {available && fileName && (
              <div className="mt-2 text-xs text-muted-foreground space-y-0.5">
                <p className="truncate" title={fileName}>{fileName}</p>
                <p className="text-[11px]">
                  {[formatBytes(note.file_size), formatDate(note.updated_at || note.created_at)].filter(Boolean).join(" · ")}
                </p>
              </div>
            )}
          </div>
        </div>

        {available ? (
          <div className="mt-4 grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!!busyMode}
              onClick={() => trigger("inline")}
              data-testid={`note-open-${note.id}`}
            >
              <Eye className="h-4 w-4" />
              {busyMode === "inline" ? "Opening..." : "Open"}
            </Button>
            <Button
              size="sm"
              disabled={!!busyMode}
              onClick={() => trigger("attachment")}
              data-testid={`note-download-${note.id}`}
            >
              <Download className="h-4 w-4" />
              {busyMode === "attachment" ? "Loading..." : "Download"}
            </Button>
          </div>
        ) : (
          <Button className="w-full mt-4" variant="outline" disabled data-testid={`note-locked-${note.id}`}>
            <Lock className="h-4 w-4" /> Not Available
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
